import { Component } from "react";
import { toast } from "sonner";

/**
 * Catches React render/commit errors and forces a clean re-mount of the
 * subtree instead of letting the dev overlay (or the white production
 * screen) swallow the page.
 *
 * Why this exists:
 *   Some browser extensions (Google Translate, Honey, Grammarly, …) mutate
 *   the DOM tree directly. When that happens between React's render and
 *   commit, React loses its reference to where to insert the next node
 *   and throws `NotFoundError: Failed to execute 'insertBefore' on
 *   'Node'`. The user sees a fatal red overlay even though the app is
 *   recoverable — we just need to ask React to re-render from scratch.
 *
 *   We can't disable the user's extensions; the realistic fix is to
 *   recover gracefully and re-mount the affected route.
 */
export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, key: 0 };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.warn("[ErrorBoundary] caught:", error?.message, info);
        const msg = String(error?.message || "");
        const isReconciliationCrash =
            msg.includes("insertBefore") ||
            msg.includes("removeChild") ||
            msg.includes("NotFoundError");
        if (isReconciliationCrash) {
            // Most likely a browser-extension-induced DOM mutation
            // (Google Translate, etc). Notify the user once and recover
            // by forcing a re-mount of the subtree.
            try {
                toast.warning(
                    "Se detectó un conflicto con una extensión del navegador. Si vuelve a ocurrir, deshabilita Google Translate u otras extensiones en este sitio.",
                    { id: "extension-conflict", duration: 6000 },
                );
            } catch {
                /* ignore — toast container may not be mounted yet */
            }
            // Re-mount on next tick so React commits the reset cleanly.
            setTimeout(() => {
                this.setState((s) => ({ hasError: false, key: s.key + 1 }));
            }, 50);
        }
    }

    render() {
        if (this.state.hasError) {
            // Render nothing for the brief moment between catch and the
            // scheduled re-mount. Avoids painting a fallback flicker.
            return null;
        }
        return <div key={this.state.key}>{this.props.children}</div>;
    }
}
